<template>
  <DashboardContainer :pageTitle="pageTitle">
    <!-- Spinner for loading state -->
    <GivarooSpinner v-if="loading" class="mt-24" />

    <template v-else>
      <!-- Date Inputs -->
      <div class="flex flex-wrap gap-4 mb-4">
        <div class="flex items-center gap-2 flex-grow">
          <label for="start" class="text-gray-700">Start Date:</label>
          <FlatPickr
            id="start"
            name="start"
            class="input--text--small placeholder:text-gray-400 date-input flex-grow"
            placeholder="Select date start"
            :value="selectedDate.date1"
            @input="handleDateChange('date1', $event.target.value)"
          />
        </div>
        <div class="flex items-center gap-2 flex-grow">
          <label for="end" class="text-gray-700">End Date:</label>
          <FlatPickr
            id="end"
            name="end"
            class="input--text--small placeholder:text-gray-400 date-input flex-grow"
            placeholder="Select date end"
            :value="selectedDate.date2"
            @input="handleDateChange('date2', $event.target.value)"
          />
        </div>
      </div>
      <!-- Overview section + KPI cards -->
      <div class="mb-4">
        <h2 class="text-xl font-semibold text-gray-800 font-jungle tracking-widest leading-relaxed">
          Overview:
        </h2>
      </div>
      <div v-if="kpis.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div
          v-for="card in kpis"
          :key="card.title"
          class="rounded-xl p-5 text-white shadow flex flex-col gap-2"
          :class="card.bg"
        >
          <div class="text-sm/5 opacity-90">{{ card.title }}</div>
          <div class="text-3xl font-bold">{{ card.displayValue }}</div>
          <div v-if="card.subtext" class="text-xs/5 opacity-90">{{ card.subtext }}</div>
        </div>
      </div>

      <!-- Charts Section for Canvassing Org Admin -->
      <template
        v-if="isCanvassingOrgAdmin || isNPOAdmin || isCanvassingOrgFieldDirector || isGivarooStaff"
      >
        <div class="flex items-center mb-4">
          <label for="start" class="text-gray-700 mr-2">Campaign Filter:</label>
          <multiselect
            v-model="selectedCampaignFilter"
            :options="campaignFilters"
            :multiple="true"
            :close-on-select="true"
            placeholder="Select Campaigns"
            selectLabel="Apply"
            deselectLabel="Remove"
            :custom-label="customLabel"
            track-by="id"
            clearable
          ></multiselect>
        </div>
        <div class="flex justify-between items-center mb-4">
          <div class="flex items-center gap-4">
            <h1
              style="
                font-family: 'JungleFever';
                font-size: 1.5rem;
                font-weight: bold;
                text-decoration: underline;
                letter-spacing: 0.1em;
              "
            >
              Campaign Metrics
            </h1>
            <RouterLink
              :to="{
                name: 'FundraiserPerformanceCharts',
                query: { start: selectedDate.date1, end: selectedDate.date2 },
              }"
              class="btn--table"
            >
              Fundraiser Metrics
            </RouterLink>
          </div>
        </div>
        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          Recurring & Success Donations
        </h2>
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <!-- Number of Donations Chart -->
          <div class="scrollable-chart-container lg:col-span-2">
            <h3 class="title font-bold mb-4 text-center">Number of Donations</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="numberOfDonationsData"
                  :options="chartOptions"
                  v-if="numberOfDonationsData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Cards Section -->
          <div class="flex flex-col gap-6 h-[500px]">
            <!-- Average Donor Age -->
            <div class="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500 flex-1">
              <h4 class="text-gray-500 text-sm">Average Donor Age</h4>
              <p class="text-2xl font-bold">{{ avgDonorAge }}</p>
            </div>

            <!-- Donations per Shift (overall) -->
            <div class="bg-white rounded-lg shadow p-4 border-l-4 border-emerald-500 flex-1">
              <h4 class="text-gray-500 text-sm">Donations per Shift</h4>
              <p class="text-2xl font-bold">{{ donationsPerShift }}</p>
            </div>

            <!-- Highest Average Donation -->
            <div class="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500 flex-1">
              <h4 class="text-gray-500 text-sm">Average Donations Amount</h4>
              <p class="text-2xl font-bold">${{ largestDonation }}</p>
            </div>

            <!-- Total Donors -->
            <div class="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500 flex-1">
              <h4 class="text-gray-500 text-sm">Total Donors</h4>
              <p class="text-2xl font-bold">{{ totalDonors }}</p>
            </div>
          </div>
        </div>

        <!-- Pie charts -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Donor Age Pie -->
          <div class="pie-chart-container">
            <h3 class="title font-bold mb-4 t">Donor Age Distribution</h3>
            <div class="chart-outer-wrapper">
              <PieChart
                :chart-data="donorAgePieChartData"
                :options="agePieChartOptions"
                v-if="donorAgePieChartData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>

          <!-- Donation Amount Pie -->
          <div class="pie-chart-container">
            <h3 class="title font-bold mb-4">Donation Amount Distribution</h3>
            <div class="chart-outer-wrapper">
              <PieChart
                :chart-data="donationAmountPieChartData"
                :options="pieChartOptions"
                v-if="donationAmountPieChartData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <!-- Campaign Metrics Section -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div
            v-for="campaign in campaignMetrics"
            :key="campaign.campaign_id"
            class="bg-white p-6 rounded-lg shadow-md"
          >
            <h3 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
              {{ campaign.campaign_name }} - {{ campaign.campaign_location }}
            </h3>
            <ul class="list-disc list-inside space-y-2">
              <li><strong>Total Shift Count:</strong> {{ campaign.total_shifts }}</li>
              <li><strong>Number of Donations:</strong> {{ campaign.number_of_donations }}</li>
              <li><strong>Per Shift Average:</strong> {{ campaign.per_shift_average }}</li>
            </ul>
          </div>
        </div>

        <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          <!-- Donors Over 30 Chart -->
          <div class="scrollable-chart-container xl:col-span-3">
            <h3 class="title font-bold mb-4 text-center">Donors Over 30</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="donorsOver30Data"
                  :options="chartOptions"
                  v-if="donorsOver30Data.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Average Donor Age Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Average Donor Age</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="averageDonorAgeData"
                  :options="chartOptions"
                  v-if="averageDonorAgeData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Average Donation Amount Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Average Donation Amount</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="averageDonationAmountData"
                  :options="chartOptions"
                  v-if="averageDonationAmountData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Number of Donors by Time Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">
              Number of Donors by {{ numberOfDonorsByTimeData.x_axis_label }}
            </h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="numberOfDonorsByTimeData"
                  :options="chartOptions"
                  v-if="numberOfDonorsByTimeData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          One-Time & Success Donations
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Number of One-Time Donations</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="numberOfOneTimeDonationsData"
                  :options="chartOptions"
                  v-if="numberOfOneTimeDonationsData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Average One-Time Donation Amount</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="averageOneTimeDonationAmountData"
                  :options="chartOptions"
                  v-if="averageOneTimeDonationAmountData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Success Rate Donut -->
          <div
            class="bg-white rounded-lg shadow p-4 border-l-4 border-teal-500 relative h-[500px] flex flex-col"
          >
            <h3 class="title font-bold mb-4 text-center">Success Rate</h3>
            <div class="flex items-center justify-center flex-grow relative">
              <PieChart :chart-data="successRateChartData" :options="successRatePieOptions" />
            </div>
            <div
              class="donut-center absolute inset-0 flex items-center justify-center flex-col text-center pointer-events-none"
            >
              <div class="text-gray-500 text-xs">Percent</div>
              <div class="text-2xl font-bold">{{ successRatePercent }}</div>
            </div>
          </div>

          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Total One-Time Donation Sum</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="totalOneTimeDonationSumData"
                  :options="chartOptions"
                  v-if="totalOneTimeDonationSumData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
          <!-- Form Type Distribution Pie Chart -->
          <div class="pie-chart-container">
            <h3 class="title font-bold mb-4 text-center">Form Type Distribution</h3>
            <div class="chart-outer-wrapper">
              <PieChart
                :chart-data="formTypePieChartData"
                :options="pieChartOptions"
                v-if="formTypePieChartData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          Shifts Count
        </h2>
        <div class="grid grid-cols-1 gap-4 mb-4">
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Shifts</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="shiftsSpentData"
                  :options="chartOptions"
                  v-if="shiftsSpentData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- M1 % Metrics Section -->
        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          M1 % Metrics
        </h2>
        <div class="grid grid-cols-1 gap-4 mb-4">
          <!-- For Admin view -->
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <h3 class="title font-bold mb-4 text-center">M1 Percentage</h3>
            <div class="text-center">
              <div class="text-4xl font-bold text-blue-600 mb-2">{{ m1Percentage }}%</div>
              <div class="text-sm text-gray-600">
                {{ m1Count }} out of {{ m1TotalCount }} donations
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          Failed Donations
        </h2>
        <div class="grid grid-cols-1 gap-4 mb-4">
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Failed Donations by Campaign</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="failedDonationsBarChartData"
                  :options="chartOptions"
                  v-if="failedDonationsBarChartData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          Donor Verification
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">DV SMS Sent</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="dvSmsSentData"
                  :options="chartOptions"
                  v-if="dvSmsSentData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">DV SMS Verified</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="dvSmsVerifiedData"
                  :options="chartOptions"
                  v-if="dvSmsVerifiedData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Charts Section for Fundraiser -->
      <template v-if="isFundraiser">
        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          Recurring Donations
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Number of Recurring Donations Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">
              Number of Recurring Donations by {{ timeGrouping }}
            </h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="recurringDonationsCountData"
                  :options="chartOptions"
                  v-if="recurringDonationsCountData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Donors Over 30 Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Donors Over 30 by {{ timeGrouping }}</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="fundraiserDonorsOver30Data"
                  :options="chartOptions"
                  v-if="fundraiserDonorsOver30Data.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add these pie charts for fundraiser -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Donor Age Pie Chart -->
          <div class="pie-chart-container">
            <h3 class="title font-bold mb-4 text-center">Donor Age Distribution</h3>
            <div class="chart-outer-wrapper">
              <PieChart
                :chart-data="fundraiserAgePieChartData"
                :options="agePieChartOptions"
                v-if="fundraiserAgePieChartData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>

          <!-- Donation Amount Pie Chart -->
          <div class="pie-chart-container">
            <h3 class="title font-bold mb-4 text-center">Donation Amount Distribution</h3>
            <div class="chart-outer-wrapper">
              <PieChart
                :chart-data="fundraiserAmountPieChartData"
                :options="pieChartOptions"
                v-if="fundraiserAmountPieChartData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
          <!-- Form Type Distribution Pie Chart -->
          <div class="pie-chart-container">
            <h3 class="title font-bold mb-4 text-center">Form Type Distribution</h3>
            <div class="chart-outer-wrapper">
              <PieChart
                :chart-data="fundraiserFormTypePieChartData"
                :options="pieChartOptions"
                v-if="fundraiserFormTypePieChartData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          One-Time Donations
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Number of One-Time Donations Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">
              Number of One-Time Donations by {{ timeGrouping }}
            </h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="oneTimeDonationsCountData"
                  :options="chartOptions"
                  v-if="oneTimeDonationsCountData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Average One-Time Donation Amount Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">
              Average One-Time Donation Amount by {{ timeGrouping }}
            </h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="avgOneTimeDonationData"
                  :options="chartOptions"
                  v-if="avgOneTimeDonationData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Total One-Time Donation Sum Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">
              Total One-Time Donation Sum by {{ timeGrouping }}
            </h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="totalOneTimeDonationData"
                  :options="chartOptions"
                  v-if="totalOneTimeDonationData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          Failed Donations
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Failed Donations Chart -->
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">Failed Donations by {{ timeGrouping }}</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="failedDonationsData"
                  :options="chartOptions"
                  v-if="failedDonationsData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add this after the existing fundraiser charts -->
        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          Donor Verification
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">DV SMS Sent by {{ timeGrouping }}</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="fundraiserDvSmsSentData"
                  :options="chartOptions"
                  v-if="fundraiserDvSmsSentData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <div class="scrollable-chart-container">
            <h3 class="title font-bold mb-4 text-center">DV SMS Verified by {{ timeGrouping }}</h3>
            <div class="chart-outer-wrapper">
              <div class="chart-inner-wrapper">
                <BarChart
                  :chart-data="fundraiserDvSmsVerifiedData"
                  :options="chartOptions"
                  v-if="fundraiserDvSmsVerifiedData.labels.length > 0"
                />
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- M1 % Metrics Section for Fundraiser -->
        <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
          M1 % Metrics
        </h2>
        <div class="grid grid-cols-1 gap-4 mb-4">
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <h3 class="title font-bold mb-4 text-center">M1 Percentage</h3>
            <div class="text-center">
              <div class="text-4xl font-bold text-blue-600 mb-2">{{ m1Percentage }}%</div>
              <div class="text-sm text-gray-600">
                {{ m1Count }} out of {{ m1TotalCount }} donations
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Back Button -->
      <div class="flex justify-end mt-10">
        <RouterLink :to="{ name: 'CampaignList' }" class="btn--table">
          Back to Campaigns
        </RouterLink>
      </div>
    </template>
  </DashboardContainer>
</template>

<script>
import { ref, onBeforeMount, reactive, computed, watch } from 'vue'
import { BarChart, PieChart } from 'vue-chart-3'
import { Chart, registerables } from 'chart.js'
import DashboardContainer from '../components/DashboardContainer.vue'
import Spinner from '@/components/Spinner'
import { DonationApi } from '@/services/donations/'
import { WorkedHoursApi } from '@/services/workingHours'
import { useStore } from 'vuex'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import { useRoute } from 'vue-router'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import { getTodayInEastern } from '@/utils/dateHelpers'

// Register Chart.js components
Chart.register(...registerables)

export default {
  name: 'CampaignCharts',
  components: {
    DashboardContainer,
    Spinner,
    BarChart,
    PieChart,
    FlatPickr,
    Multiselect,
    GivarooSpinner,
  },
  setup() {
    const store = useStore()
    const isCanvassingOrgAdmin = ref(store.getters.isCanvassingOrgAdmin)
    const isCanvassingOrgFieldDirector = ref(store.getters.isCanvassingOrgFieldDirector)
    const isFundraiser = ref(store.getters.isFundraiser)
    const isNPOAdmin = ref(store.getters.isNPOAdmin)
    const isGivarooStaff = ref(store.getters.isGivarooStaff)
    const route = useRoute()

    const loading = ref(false)
    const today = getTodayInEastern()
    const selectedDate = reactive({
      date1: today,
      date2: '',
    })
    const pageTitle = ref('Campaign Charts')
    const timeGrouping = ref('Day')
    const fundraiserMetrics = ref(null)
    const filterIndexes = reactive({
      campaigns: [],
    })
    const campaignFilters = computed(() => filterIndexes.campaigns)
    const selectedCampaignFilter = ref([])

    // UI helpers
    const kpis = ref([])
    const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value || 0))
    const formatCurrency = (value) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(Number(value || 0))

    // Stats for cards
    const avgDonorAge = computed(() => {
      const values = averageDonorAgeData.value.datasets?.[0]?.data || []
      const validValues = values.filter((age) => age > 0)
      if (!validValues.length) return 0
      const sum = validValues.reduce((a, b) => a + Number(b || 0), 0)
      return Math.round((sum / validValues.length) * 10) / 10
    })

    const donationsPerShift = computed(() => {
      const recurring = (numberOfDonationsData.value.datasets?.[0]?.data || []).reduce(
        (a, b) => a + Number(b || 0),
        0,
      )
      const oneTime = (numberOfOneTimeDonationsData.value.datasets?.[0]?.data || []).reduce(
        (a, b) => a + Number(b || 0),
        0,
      )
      const totalDonations = recurring + oneTime
      const totalShifts = (shiftsSpentData.value.datasets?.[0]?.data || []).reduce(
        (a, b) => a + Number(b || 0),
        0,
      )
      if (!totalShifts) return 0
      return Math.round((totalDonations / totalShifts) * 100) / 100
    })

    const largestDonation = computed(() => {
      // by looking at the average one-time donation amounts across campaigns
      const oneTimeAverages = averageOneTimeDonationAmountData.value.datasets?.[0]?.data || []
      const recurringAverages = averageDonationAmountData.value.datasets?.[0]?.data || []

      // Combine one-time and recurring averages to find the highest average amount
      const allAverages = [...oneTimeAverages, ...recurringAverages]
      // Filter out zero values
      const validAverages = allAverages.filter((amount) => amount > 0)

      if (!validAverages.length) return 0
      // Calculate sum of all valid averages
      const sum = validAverages.reduce((a, b) => a + Number(b || 0), 0)
      // Calculate average of all campaigns
      const averageAmount = sum / validAverages.length

      return formatNumber(Math.round(averageAmount))
    })

    const totalDonors = computed(() => {
      const donorCounts = numberOfDonorsByTimeData.value.datasets?.[0]?.data || []
      if (!donorCounts.length) return 0
      const total = donorCounts.reduce((sum, count) => sum + Number(count || 0), 0)
      return formatNumber(total)
    })

    const pieChartOptions = ref({
      responsive: true,
      maintainAspectRatio: false, // This is already set correctly
      aspectRatio: 1, // You can adjust this to change the pie chart's proportions
      plugins: {
        legend: {
          position: 'bottom',
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.label || ''
              const value = context.raw || 0
              // Show percentage for form type charts
              if (label === 'QR Code' || label === 'Fundraiser Tablet') {
                return `${label}: ${value.toFixed(1)}%`
              }
              return `${label}: ${value}`
            },
          },
        },
      },
    })
    const agePieChartOptions = ref({
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1,
      plugins: {
        legend: {
          position: 'bottom',
          onClick: function (e, legendItem, legend) {
            const chart = legend.chart
            const labels = chart.data.labels
            const meta = chart.getDatasetMeta(0)

            // Toggle visibility of all slices in this age group
            labels.forEach((label, i) => {
              const age = parseInt(label)
              let shouldToggle = false

              // Check if this age belongs to the clicked group
              if (age >= 25 && age <= 29 && legendItem.text.includes('25-29')) {
                shouldToggle = true
              } else if (age >= 30 && age <= 39 && legendItem.text.includes('30-39')) {
                shouldToggle = true
              } else if (age >= 40 && age <= 49 && legendItem.text.includes('40-49')) {
                shouldToggle = true
              } else if (age >= 50 && age <= 59 && legendItem.text.includes('50-59')) {
                shouldToggle = true
              } else if (age >= 65 && legendItem.text.includes('65+')) {
                shouldToggle = true
              }

              if (shouldToggle) {
                meta.data[i].hidden = !meta.data[i].hidden
              }
            })

            chart.update()
          },
          labels: {
            generateLabels: function (chart) {
              const grouped = {}
              const meta = chart.getDatasetMeta(0)

              chart.data.labels.forEach((label, index) => {
                const age = parseInt(label)
                const value = chart.data.datasets[0].data[index] || 0

                if (age >= 25 && age <= 29) {
                  grouped['25-29'] = (grouped['25-29'] || 0) + value
                } else if (age >= 30 && age <= 39) {
                  grouped['30-39'] = (grouped['30-39'] || 0) + value
                } else if (age >= 40 && age <= 49) {
                  grouped['40-49'] = (grouped['40-49'] || 0) + value
                } else if (age >= 50 && age <= 59) {
                  grouped['50-59'] = (grouped['50-59'] || 0) + value
                } else if (age >= 65) {
                  grouped['65+'] = (grouped['65+'] || 0) + value
                }
              })

              return Object.entries(grouped).map(([label, value], index) => {
                // Check if all slices in this group are hidden
                const labels = chart.data.labels
                let allHidden = true

                labels.forEach((labelItem) => {
                  const age = parseInt(labelItem)
                  let belongsToGroup = false

                  if (age >= 25 && age <= 29 && label === '25-29') {
                    belongsToGroup = true
                  } else if (age >= 30 && age <= 39 && label === '30-39') {
                    belongsToGroup = true
                  } else if (age >= 40 && age <= 49 && label === '40-49') {
                    belongsToGroup = true
                  } else if (age >= 50 && age <= 59 && label === '50-59') {
                    belongsToGroup = true
                  } else if (age >= 65 && label === '65+') {
                    belongsToGroup = true
                  }

                  if (belongsToGroup) {
                    const labelIndex = labels.indexOf(labelItem)
                    if (!meta.data[labelIndex].hidden) {
                      allHidden = false
                    }
                  }
                })

                return {
                  text: label,
                  fillStyle:
                    label === '25-29'
                      ? '#36A2EB'
                      : label === '30-39'
                      ? '#FFCE56'
                      : label === '40-49'
                      ? '#4BC0C0'
                      : label === '50-59'
                      ? '#9966FF'
                      : '#FF9F40',
                  strokeStyle:
                    label === '25-29'
                      ? '#36A2EB'
                      : label === '30-39'
                      ? '#FFCE56'
                      : label === '40-49'
                      ? '#4BC0C0'
                      : label === '50-59'
                      ? '#9966FF'
                      : '#FF9F40',
                  lineWidth: 2,
                  hidden: allHidden,
                  index: index,
                }
              })
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.label || ''
              const value = context.raw || 0
              return `${label}: ${value}`
            },
          },
        },
      },
    })

    // Success Rate Donut
    const successRatePercent = ref(0)
    const successRateChartData = ref({
      labels: ['Successful', 'Failed'],
      datasets: [
        {
          data: [0, 0],
          backgroundColor: ['#10b981', '#e5e7eb'],
          borderWidth: 0,
        },
      ],
    })
    const successRatePieOptions = ref({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: { legend: { display: false } },
    })

    // M1 Percentage data
    const m1Percentage = ref(0)
    const m1Count = ref(0)
    const m1TotalCount = ref(0)

    // Chart data refs for Canvassing Org Admin
    const numberOfDonationsData = ref({
      labels: [],
      datasets: [
        {
          label: 'Number of Donations',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const donorsOver30Data = ref({
      labels: [],
      datasets: [
        {
          label: 'Donors Over 30',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const averageDonorAgeData = ref({
      labels: [],
      datasets: [
        {
          label: 'Average Donor Age',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const averageDonationAmountData = ref({
      labels: [],
      datasets: [
        {
          label: 'Average Donation Amount',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const donorAgePieChartData = ref({
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            // 25-29
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            // 30-39
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            // 40+
            '#4BC0C0',
            '#9966FF',
            '#FF9F40',
          ],
        },
      ],
    })

    const donationAmountPieChartData = ref({
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            '#9966FF',
            '#00FF00',
            '#0000FF',
            '#FFFF00',
            '#FF00FF',
            '#00FFFF',
            '#FFA500',
            '#800080',
            '#32CD32',
            '#FFC0CB',
            '#008080',
            '#FF0000',
          ],
        },
      ],
    })

    const fundraiserAgePieChartData = ref({
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            // 25-29
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            // 30-39
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            // 40+
            '#4BC0C0',
            '#9966FF',
            '#FF9F40',
          ],
        },
      ],
    })

    const fundraiserAmountPieChartData = ref({
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            '#FF0000',
            '#00FF00',
            '#0000FF',
            '#FFFF00',
            '#FF00FF',
            '#00FFFF',
            '#FFA500',
            '#800080',
            '#32CD32',
            '#FFC0CB',
            '#008080',
            '#A52A2A',
          ],
        },
      ],
    })

    const numberOfOneTimeDonationsData = ref({
      labels: [],
      datasets: [
        {
          label: 'Number of One-Time Donations',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const averageOneTimeDonationAmountData = ref({
      labels: [],
      datasets: [
        {
          label: 'Average One-Time Donation Amount',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const totalOneTimeDonationSumData = ref({
      labels: [],
      datasets: [
        {
          label: 'Total One-Time Donation Sum',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const failedDonationsBarChartData = ref({
      labels: [],
      datasets: [
        {
          label: 'Failed Donations',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const shiftsSpentData = ref({
      labels: [],
      datasets: [
        {
          label: 'Shifts Spent',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const numberOfDonorsByTimeData = ref({
      labels: [],
      datasets: [
        {
          label: 'Number of Donors',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
      x_axis_label: 'Hour',
    })

    const dvSmsSentData = ref({
      labels: [],
      datasets: [
        {
          label: 'DV SMS Sent',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const dvSmsVerifiedData = ref({
      labels: [],
      datasets: [
        {
          label: 'DV SMS Verified',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    // Chart data refs for Fundraiser
    const recurringDonationsCountData = ref({
      labels: [],
      datasets: [
        {
          label: 'Recurring Donations',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const fundraiserDonorsOver30Data = ref({
      labels: [],
      datasets: [
        {
          label: 'Donors Over 30',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const oneTimeDonationsCountData = ref({
      labels: [],
      datasets: [
        {
          label: 'One-Time Donations',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const avgOneTimeDonationData = ref({
      labels: [],
      datasets: [
        {
          label: 'Average Amount',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const totalOneTimeDonationData = ref({
      labels: [],
      datasets: [
        {
          label: 'Total Amount',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const failedDonationsData = ref({
      labels: [],
      datasets: [
        {
          label: 'Failed Donations',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const fundraiserDvSmsSentData = ref({
      labels: [],
      datasets: [
        {
          label: 'DV SMS Sent',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const fundraiserDvSmsVerifiedData = ref({
      labels: [],
      datasets: [
        {
          label: 'DV SMS Verified',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const campaignMetrics = ref([])

    // Form Type Pie Chart Data for Canvassing Org Admin
    const formTypePieChartData = ref({
      labels: ['QR Code', 'Fundraiser Tablet'],
      datasets: [
        {
          data: [0, 0],
          backgroundColor: ['#2596be', '#10b981'],
        },
      ],
    })

    // Form Type Pie Chart Data for Fundraiser
    const fundraiserFormTypePieChartData = ref({
      labels: ['QR Code', 'Fundraiser Tablet'],
      datasets: [
        {
          data: [0, 0],
          backgroundColor: ['#2596be', '#10b981'],
        },
      ],
    })

    // Chart options
    const chartOptions = ref({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 0, right: 0, top: 0, bottom: 10 } },

      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0,0,0,0.06)',
            drawBorder: false,
          },
          ticks: {
            callback: (val) => new Intl.NumberFormat('en-US').format(val),
            color: '#6b7280',
            font: {
              family: 'JungleFever',
              size: 12,
            },
          },
        },
        x: {
          grid: { display: false, drawBorder: false },
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 45,
            color: '#6b7280',
            categoryPercentage: 1.0,
            font: {
              family: 'JungleFever',
              size: 12,
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
          position: 'top',
          labels: { usePointStyle: true, boxWidth: 8, color: '#374151' },
        },
        tooltip: {
          backgroundColor: 'rgba(31,41,55,0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || ''
              const value = context.raw || 0
              return `${label}: ${new Intl.NumberFormat('en-US').format(value)}`
            },
          },
        },
      },
      elements: {
        bar: {
          borderRadius: 8,
        },
      },
    })

    const buildAdminKpis = (campaignMetricsResponse, shiftsSpentResponse) => {
      const totalRecurringDonations = campaignMetricsResponse.reduce(
        (sum, c) => sum + Number(c.recurring_metrics?.number_of_donations || 0),
        0,
      )
      const totalOneTimeDonations = campaignMetricsResponse.reduce(
        (sum, c) => sum + Number(c.one_time_metrics?.number_of_donations || 0),
        0,
      )
      const totalOneTimeSum = campaignMetricsResponse.reduce(
        (sum, c) => sum + Number(c.one_time_metrics?.total_donation_sum || 0),
        0,
      )
      const totalShifts = (shiftsSpentResponse || []).reduce(
        (sum, s) => sum + Number(s.total_shifts || 0),
        0,
      )
      return [
        {
          title: 'Total Recurring Donations',
          displayValue: formatNumber(totalRecurringDonations),
          bg: 'bg-blue-500',
          subtext: 'For selected range',
        },
        {
          title: 'Total One-Time Donations',
          displayValue: formatNumber(totalOneTimeDonations),
          bg: 'bg-yellow-500',
          subtext: 'For selected range',
        },
        {
          title: 'Total One-Time Sum',
          displayValue: formatCurrency(totalOneTimeSum),
          bg: 'bg-cyan-500',
          subtext: 'Gross amount',
        },
        {
          title: 'Total Shifts',
          displayValue: formatNumber(totalShifts),
          bg: 'bg-green-500',
          subtext: 'For selected range',
        },
      ]
    }

    const buildFundraiserKpis = (response) => {
      const totalRecurring = (response?.recurring_donations?.by_time || []).reduce(
        (sum, i) => sum + Number(i.recurring_count || 0),
        0,
      )
      const totalOneTime = (response?.one_time_donations?.by_time || []).reduce(
        (sum, i) => sum + Number(i.one_time_count || 0),
        0,
      )
      const totalOneTimeSum = (response?.one_time_donations?.by_time || []).reduce(
        (sum, i) => sum + Number(i.total_one_time || 0),
        0,
      )
      const totalFailed = (response?.failed_donations?.by_time || []).reduce(
        (sum, i) => sum + Number(i.failed_count || 0),
        0,
      )

      return [
        {
          title: 'Total Recurring Donations',
          displayValue: formatNumber(totalRecurring),
          bg: 'bg-blue-500',
          subtext: 'For selected range',
        },
        {
          title: 'Total One-Time Donations',
          displayValue: formatNumber(totalOneTime),
          bg: 'bg-yellow-500',
          subtext: 'For selected range',
        },
        {
          title: 'Total One-Time Sum',
          displayValue: formatCurrency(totalOneTimeSum),
          bg: 'bg-cyan-500',
          subtext: 'Gross amount',
        },
        {
          title: 'Failed Donations',
          displayValue: formatNumber(totalFailed),
          bg: 'bg-green-500',
          subtext: 'Across all time groups',
        },
      ]
    }

    // Calculate time grouping based on date range
    const calculateTimeGrouping = (startDate, endDate) => {
      if (!startDate || !endDate) return 'Day'

      const start = new Date(startDate)
      const end = new Date(endDate)
      const diffTime = Math.abs(end - start)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      return diffDays > 7 ? 'Week' : 'Day'
    }

    // Keep campaigns that had fundraiser shifts in the selected date range
    const filterCampaignsWithShifts = (campaignMetricsResponse, shiftsSpentResponse) => {
      const campaignsWithShifts = new Set(
        (shiftsSpentResponse || [])
          .filter((c) => Number(c.total_shifts) > 0)
          .map((c) => String(c.campaign_id)),
      )

      return {
        metrics: (campaignMetricsResponse || []).filter((c) =>
          campaignsWithShifts.has(String(c.campaign_id)),
        ),
        shifts: (shiftsSpentResponse || []).filter((c) => Number(c.total_shifts) > 0),
      }
    }

    // Fetch data for Canvassing Org Admin
    const fetchCanvassingOrgData = async (startDate, endDate, campaignFilterValue) => {
      try {
        loading.value = true
        const campaignMetricsResponse = await DonationApi.csc.get_campaign_performance_metrics({
          startDate,
          endDate,
          campaignFilterValue,
        })
        console.log(campaignMetricsResponse)

        // Fetch shifts early so bar charts can exclude campaigns with no assignments
        const shiftsSpentResponse = await WorkedHoursApi.csc.shifts_spent_per_campaign({
          startDate,
          endDate,
          campaignFilterValue,
        })
        const { metrics: filteredMetrics, shifts: filteredShifts } = filterCampaignsWithShifts(
          campaignMetricsResponse,
          shiftsSpentResponse,
        )

        campaignMetrics.value = filteredMetrics.map((campaign) => ({
          campaign_id: campaign.campaign_id,
          campaign_name: campaign.campaign_name,
          campaign_location: campaign.campaign_location,
          number_of_donations: campaign.recurring_metrics.number_of_donations,
          donors_over_30: campaign.donor_demographics.donors_over_30,
          average_donor_age: campaign.donor_demographics.average_donor_age,
          average_donation_amount: campaign.recurring_metrics.average_donation_amount,
          number_of_one_time_donations: campaign.one_time_metrics.number_of_donations,
          average_one_time_donation_amount: campaign.one_time_metrics.average_donation_amount,
          total_one_time_donation_sum: campaign.one_time_metrics.total_donation_sum,
          number_of_failed_donations: campaign.failed_donations_count,
        }))

        // Update all chart data from campaigns that had shifts in range
        numberOfDonationsData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        numberOfDonationsData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.recurring_metrics.number_of_donations,
        )

        donorsOver30Data.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        donorsOver30Data.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.donor_demographics.donors_over_30,
        )

        averageDonorAgeData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        averageDonorAgeData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.donor_demographics.average_donor_age,
        )

        averageDonationAmountData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        averageDonationAmountData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.recurring_metrics.average_donation_amount,
        )

        numberOfOneTimeDonationsData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        numberOfOneTimeDonationsData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.one_time_metrics.number_of_donations,
        )

        averageOneTimeDonationAmountData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        averageOneTimeDonationAmountData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.one_time_metrics.average_donation_amount,
        )

        totalOneTimeDonationSumData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        totalOneTimeDonationSumData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.one_time_metrics.total_donation_sum,
        )

        failedDonationsBarChartData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        failedDonationsBarChartData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.failed_donations_count,
        )
        // DV SMS charts
        dvSmsSentData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        dvSmsSentData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.dv_sms_metrics?.sms_sent_count || 0,
        )

        dvSmsVerifiedData.value.labels = filteredMetrics.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        dvSmsVerifiedData.value.datasets[0].data = filteredMetrics.map(
          (campaign) => campaign.dv_sms_metrics?.sms_verified_count || 0,
        )

        // Pie charts
        const donorAgePieChartResponse = await DonationApi.csc.donor_age_pie_chart({
          startDate,
          endDate,
          campaignFilterValue,
        })
        donorAgePieChartData.value.labels = donorAgePieChartResponse.map((item) => item.label)
        donorAgePieChartData.value.datasets[0].data = donorAgePieChartResponse.map(
          (item) => item.value,
        )

        const donationAmountPieChartResponse = await DonationApi.csc.donation_amount_pie_chart({
          startDate,
          endDate,
          campaignFilterValue,
        })
        donationAmountPieChartData.value.labels = donationAmountPieChartResponse.map(
          (item) => item.label,
        )
        donationAmountPieChartData.value.datasets[0].data = donationAmountPieChartResponse.map(
          (item) => item.value,
        )

        // Form Type Pie Chart Data
        const formTypeResponse = await DonationApi.csc.form_type_pie_chart({
          startDate,
          endDate,
          campaignFilterValue,
        })
        formTypePieChartData.value.labels = formTypeResponse.map((item) => item.label)
        formTypePieChartData.value.datasets[0].data = formTypeResponse.map((item) => item.value)

        const numberOfDonorsByTimeResponse = await DonationApi.csc.number_of_donors_by_time({
          startDate,
          endDate,
          campaignFilterValue,
        })
        numberOfDonorsByTimeData.value = {
          labels: numberOfDonorsByTimeResponse.data.map((item) => item.time_group),
          datasets: [
            {
              label: 'Number of Donors',
              backgroundColor: '#2596be',
              data: numberOfDonorsByTimeResponse.data.map((item) => item.count),
            },
          ],
          x_axis_label: numberOfDonorsByTimeResponse.x_axis_label,
        }

        shiftsSpentData.value.labels = filteredShifts.map(
          (campaign) => `${campaign.campaign_name} - ${campaign.campaign_location}`,
        )
        shiftsSpentData.value.datasets[0].data = filteredShifts.map(
          (campaign) => campaign.total_shifts,
        )

        // Update campaign metrics with additional data
        campaignMetrics.value = campaignMetrics.value.map((campaign) => {
          const shiftsSpent = filteredShifts.find(
            (shift) => String(shift.campaign_id) === String(campaign.campaign_id),
          )

          const perShiftAverage =
            shiftsSpent && shiftsSpent.total_shifts > 0
              ? (campaign.number_of_donations / shiftsSpent.total_shifts).toFixed(2)
              : 0

          return {
            ...campaign,
            // fundraiser_count: fundraiserCount ? fundraiserCount.fundraiser_count : 0,
            total_shifts: shiftsSpent ? shiftsSpent.total_shifts : 0,
            per_shift_average: perShiftAverage,
          }
        })

        // Build KPI cards for admin/npo
        kpis.value = buildAdminKpis(campaignMetricsResponse, shiftsSpentResponse)

        // Compute success rate across all campaigns
        const totalRecurring = campaignMetricsResponse.reduce(
          (sum, c) => sum + Number(c.recurring_metrics?.number_of_donations || 0),
          0,
        )
        const totalOneTime = campaignMetricsResponse.reduce(
          (sum, c) => sum + Number(c.one_time_metrics?.number_of_donations || 0),
          0,
        )
        const totalFailed = campaignMetricsResponse.reduce(
          (sum, c) => sum + Number(c.failed_donations_count || 0),
          0,
        )
        const totalAttempts = totalRecurring + totalOneTime + totalFailed
        const successful = totalRecurring + totalOneTime
        successRatePercent.value = totalAttempts
          ? Math.round((successful / totalAttempts) * 100)
          : 0
        successRateChartData.value.datasets[0].data = [successful, totalAttempts - successful]

        // Fetch M1 percentage for admin
        try {
          const m1Response = await DonationApi.csc.getM1Percentage({
            startDate,
            endDate,
            campaignFilterValue,
          })
          m1Percentage.value = m1Response.m1_percentage || 0
          m1Count.value = m1Response.m1_count || 0
          m1TotalCount.value = m1Response.total_count || 0
        } catch (error) {
          console.error('Error fetching M1 percentage:', error)
          m1Percentage.value = 0
        }
      } catch (error) {
        console.error('Error fetching canvassing org data:', error)
        alert('Failed to fetch canvassing org data')
      } finally {
        loading.value = false
      }
    }

    // Fetch data for Fundraiser
    const fetchFundraiserData = async (startDate, endDate) => {
      try {
        loading.value = true
        const response = await DonationApi.csc.fundraiser_metrics({
          startDate,
          endDate,
        })
        const pieResponse = await DonationApi.csc.donor_analytics_pie_charts_for_fundraisers({
          start_date: startDate,
          end_date: endDate,
        })

        if (pieResponse.age_distribution) {
          fundraiserAgePieChartData.value.labels = pieResponse.age_distribution.map(
            (item) => item.label,
          )
          fundraiserAgePieChartData.value.datasets[0].data = pieResponse.age_distribution.map(
            (item) => item.value,
          )
        }

        if (pieResponse.amount_distribution) {
          fundraiserAmountPieChartData.value.labels = pieResponse.amount_distribution.map(
            (item) => item.label,
          )
          fundraiserAmountPieChartData.value.datasets[0].data = pieResponse.amount_distribution.map(
            (item) => item.value,
          )
        }

        fundraiserMetrics.value = response
        timeGrouping.value = calculateTimeGrouping(startDate, endDate)

        // Update recurring donations data
        recurringDonationsCountData.value = {
          labels: response.recurring_donations.by_time.map((item) => item.time_group),
          datasets: [
            {
              label: 'Recurring Donations',
              backgroundColor: '#2596be',
              borderRadius: 8,
              maxBarThickness: 32,
              data: response.recurring_donations.by_time.map((item) => item.recurring_count),
            },
          ],
        }

        // Update donors over 30 data
        fundraiserDonorsOver30Data.value = {
          labels: response.recurring_donations.by_time.map((item) => item.time_group),
          datasets: [
            {
              label: 'Donors Over 30',
              backgroundColor: '#2596be',
              borderRadius: 8,
              maxBarThickness: 32,
              data: response.recurring_donations.by_time.map((item) => item.donors_over_30),
            },
          ],
        }

        // Update one-time donations data
        oneTimeDonationsCountData.value = {
          labels: response.one_time_donations.by_time.map((item) => item.time_group),
          datasets: [
            {
              label: 'One-Time Donations',
              backgroundColor: '#2596be',
              borderRadius: 8,
              maxBarThickness: 32,
              data: response.one_time_donations.by_time.map((item) => item.one_time_count),
            },
          ],
        }

        // Update average one-time donation data
        avgOneTimeDonationData.value = {
          labels: response.one_time_donations.by_time.map((item) => item.time_group),
          datasets: [
            {
              label: 'Average Amount',
              backgroundColor: '#2596be',
              borderRadius: 8,
              maxBarThickness: 32,
              data: response.one_time_donations.by_time.map((item) => item.avg_one_time),
            },
          ],
        }

        // Update total one-time donation data
        totalOneTimeDonationData.value = {
          labels: response.one_time_donations.by_time.map((item) => item.time_group),
          datasets: [
            {
              label: 'Total Amount',
              backgroundColor: '#2596be',
              borderRadius: 8,
              maxBarThickness: 32,
              data: response.one_time_donations.by_time.map((item) => item.total_one_time),
            },
          ],
        }

        // Update failed donations data
        failedDonationsData.value = {
          labels: response.failed_donations.by_time.map((item) => item.time_group),
          datasets: [
            {
              label: 'Failed Donations',
              backgroundColor: '#2596be',
              borderRadius: 8,
              maxBarThickness: 32,
              data: response.failed_donations.by_time.map((item) => item.failed_count),
            },
          ],
        }

        // Form Type Pie Chart Data for Fundraiser
        const fundraiserFormTypeResponse = await DonationApi.csc.form_type_pie_chart({
          startDate,
          endDate,
        })
        fundraiserFormTypePieChartData.value.labels = fundraiserFormTypeResponse.map(
          (item) => item.label,
        )
        fundraiserFormTypePieChartData.value.datasets[0].data = fundraiserFormTypeResponse.map(
          (item) => item.value,
        )

        // DV SMS charts for fundraiser
        if (response.dv_sms_metrics) {
          // Since this is a single fundraiser, we'll show the total counts
          fundraiserDvSmsSentData.value = {
            labels: ['Total DV SMS Sent'],
            datasets: [
              {
                label: 'DV SMS Sent',
                backgroundColor: '#2596be',
                borderRadius: 8,
                maxBarThickness: 32,
                data: [response.dv_sms_metrics.sms_sent_count || 0],
              },
            ],
          }

          fundraiserDvSmsVerifiedData.value = {
            labels: ['Total DV SMS Verified'],
            datasets: [
              {
                label: 'DV SMS Verified',
                backgroundColor: '#2596be',
                borderRadius: 8,
                maxBarThickness: 32,
                data: [response.dv_sms_metrics.sms_verified_count || 0],
              },
            ],
          }
        }

        // Build KPI cards for fundraiser only when user is not admin/field director
        if (
          !isCanvassingOrgAdmin.value &&
          !isNPOAdmin.value &&
          !isCanvassingOrgFieldDirector.value &&
          !isGivarooStaff.value
        ) {
          kpis.value = buildFundraiserKpis(response)
        }

        // Compute success rate for fundraiser
        const fTotalRecurring = (response?.recurring_donations?.by_time || []).reduce(
          (sum, i) => sum + Number(i.recurring_count || 0),
          0,
        )
        const fTotalOneTime = (response?.one_time_donations?.by_time || []).reduce(
          (sum, i) => sum + Number(i.one_time_count || 0),
          0,
        )
        const fTotalFailed = (response?.failed_donations?.by_time || []).reduce(
          (sum, i) => sum + Number(i.failed_count || 0),
          0,
        )
        const fTotalAttempts = fTotalRecurring + fTotalOneTime + fTotalFailed
        const fSuccessful = fTotalRecurring + fTotalOneTime
        successRatePercent.value = fTotalAttempts
          ? Math.round((fSuccessful / fTotalAttempts) * 100)
          : 0
        successRateChartData.value.datasets[0].data = [fSuccessful, fTotalAttempts - fSuccessful]

        // Fetch M1 percentage for fundraiser
        try {
          const m1Response = await DonationApi.csc.getM1Percentage({
            startDate,
            endDate,
          })
          m1Percentage.value = m1Response.m1_percentage || 0
          m1Count.value = m1Response.m1_count || 0
          m1TotalCount.value = m1Response.total_count || 0
        } catch (error) {
          console.error('Error fetching M1 percentage:', error)
          m1Percentage.value = 0
        }
      } catch (error) {
        console.error('Error fetching fundraiser data:', error)
        alert('Failed to fetch fundraiser data')
      } finally {
        loading.value = false
      }
    }

    // Handle date change
    const handleDateChange = (dateField, value) => {
      selectedDate[dateField] = value
      if (
        isCanvassingOrgAdmin.value ||
        isNPOAdmin.value ||
        isCanvassingOrgFieldDirector.value ||
        isGivarooStaff.value
      ) {
        fetchCanvassingOrgData(selectedDate.date1, selectedDate.date2, campaignFilterValue.value)
      }
      if (isFundraiser.value) {
        fetchFundraiserData(selectedDate.date1, selectedDate.date2)
      }
    }
    const campaignFilterValue = ref('')
    const setFilter = () => {
      campaignFilterValue.value = selectedCampaignFilter.value
        .map((campaign) => campaign.id)
        .join(',')
      if (
        isCanvassingOrgAdmin.value ||
        isNPOAdmin.value ||
        isCanvassingOrgFieldDirector.value ||
        isGivarooStaff.value
      ) {
        fetchCanvassingOrgData(selectedDate.date1, selectedDate.date2, campaignFilterValue.value)
      }
    }
    const customLabel = (option) => {
      return `${option.name} - ${option.location}`
    }

    onBeforeMount(async () => {
      if (route.query.start) selectedDate.date1 = route.query.start
      if (route.query.end) selectedDate.date2 = route.query.end

      if (
        isCanvassingOrgAdmin.value ||
        isNPOAdmin.value ||
        isCanvassingOrgFieldDirector.value ||
        isGivarooStaff.value
      ) {
        await fetchCanvassingOrgData(
          selectedDate.date1,
          selectedDate.date2,
          campaignFilterValue.value,
        )
        const response = await DonationApi.csc.retrieveFilterIndexes()
        filterIndexes.campaigns = response.campaigns || []
      }
      if (isFundraiser.value) {
        await fetchFundraiserData(selectedDate.date1, selectedDate.date2)
      }
    })
    watch(
      [() => selectedCampaignFilter.value],
      () => {
        setFilter()
      },
      { immediate: true },
    )

    return {
      loading,
      pageTitle,
      chartOptions,
      pieChartOptions,
      agePieChartOptions,
      selectedDate,
      handleDateChange,
      isCanvassingOrgAdmin,
      isCanvassingOrgFieldDirector,
      isNPOAdmin,
      isGivarooStaff,
      isFundraiser,
      timeGrouping,
      // Canvassing Org Admin data
      numberOfDonationsData,
      donorsOver30Data,
      averageDonorAgeData,
      averageDonationAmountData,
      donorAgePieChartData,
      donationAmountPieChartData,
      numberOfOneTimeDonationsData,
      averageOneTimeDonationAmountData,
      totalOneTimeDonationSumData,
      shiftsSpentData,
      numberOfDonorsByTimeData,
      campaignMetrics,
      failedDonationsBarChartData,
      selectedCampaignFilter,
      campaignFilters,
      customLabel,
      // Fundraiser data
      recurringDonationsCountData,
      fundraiserDonorsOver30Data,
      oneTimeDonationsCountData,
      avgOneTimeDonationData,
      totalOneTimeDonationData,
      failedDonationsData,
      fundraiserAgePieChartData,
      fundraiserAmountPieChartData,
      kpis,
      avgDonorAge,
      donationsPerShift,
      largestDonation,
      totalDonors,
      successRatePercent,
      successRateChartData,
      successRatePieOptions,
      formTypePieChartData,
      fundraiserFormTypePieChartData,
      dvSmsSentData,
      dvSmsVerifiedData,
      fundraiserDvSmsSentData,
      fundraiserDvSmsVerifiedData,
      m1Percentage,
      m1Count,
      m1TotalCount,
    }
  },
}
</script>

<style scoped>
.multiselect {
  width: 378px;
}
.scrollable-chart-container {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 500px;
  display: flex;
  flex-direction: column;
}
.pie-chart-container {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 500px;
  display: flex;
  flex-direction: column;
}

/* New compact cards for the 3-column row */
.pie-card {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 380px;
  display: flex;
  flex-direction: column;
}

.metrics-card {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 380px;
  display: flex;
  flex-direction: column;
}

.metrics-scroll {
  overflow-y: auto;
  flex-grow: 1;
}

.chart-outer-wrapper {
  flex-grow: 1;
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
}

.chart-inner-wrapper {
  height: 100%;
  min-width: 500px;
  margin: 0 auto;
}

/* Scrollbar styling */
.chart-outer-wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.chart-outer-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.chart-outer-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.chart-outer-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.title {
  font-size: 1.1rem;
  color: #333;
}
</style>
